/**
 * 업무 프로젝트 대시보드(J4c2b) — 숫자 카드 4개(누르면 그 조건으로 리스트) · 완료율 도넛 · 멤버별 현황 · 지연·임박 업무.
 * 숫자는 서버 집계(/summary)만 쓴다(리스트의 200건으로 세지 않는다).
 * 도넛은 SVG 원으로 그린다(recharts 안 씀 — jsdom 시험에서 크기 0).
 *
 * - 🔴 응답을 정규화한다 — 모양이 다른 응답(e2e 모의가 모르는 경로에 `{items, total}` 을 돌려준다)에서
 *   `data.counts.total` 을 그대로 읽으면 TypeError 로 화면 전체가 오류 경계에 갈린다 → 모르는 칸은 0·빈 배열.
 * - 🔴 열 수는 뷰포트 판정(useIsMobile)이 아니라 CSS grid auto-fit 이 정한다 — 게이트웨이 사이드바 238(창 1201 이상 기본 폭 ·
 *   f8b1) + 안쪽 사이드바 240 때문에 1280 화면에서도 본문은 약 730 이다. 최소폭은 min(…, 100%) 로 감싸 좁은 화면에서
 *   카드가 본문 밖으로 넘치지 않게 한다.
 */
import { useEffect, useRef, useState } from 'react';
import { errorText } from './errorText';
import { taskPmAPI } from '../../api/client';
import { COLORS, badge, card } from '../../utils/uiStyles';

// 멤버별 현황은 위에서 이만큼만 먼저 보인다(넘치면 «전체보기»).
const MEMBER_ROWS = 6;
// 도넛 — 반지름 80 · 두께 24 · 둘레 C.
const RADIUS = 80;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const isObj = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

// 서버 응답 → 그릴 수 있는 모양(모르는 칸은 0 · 빈 배열 · alerts_total 은 없으면 alerts 길이).
function normalize(raw) {
  const d = isObj(raw) ? raw : {};
  const c = isObj(d.counts) ? d.counts : {};
  const members = (Array.isArray(d.members) ? d.members : []).filter(isObj);
  const alerts = (Array.isArray(d.alerts) ? d.alerts : []).filter(isObj);
  return {
    counts: { total: num(c.total), done: num(c.done), in_progress: num(c.in_progress), overdue: num(c.overdue) },
    members,
    alerts,
    alertsTotal: typeof d.alerts_total === 'number' && Number.isFinite(d.alerts_total) ? d.alerts_total : alerts.length,
  };
}

// 🔴 «기한 지남» 라벨은 리스트 빠른 필터와 같은 글자다(누르면 그 필터로 리스트가 열린다).
const CARDS = [
  { key: 'total', label: '전체 업무', quick: '', color: COLORS.text },
  { key: 'done', label: '완료', quick: 'done', color: COLORS.success },
  // 리스트 빠른 필터에 «기한 지남 제외 미완료» 가 없다 — 카드 숫자와 리스트 건수가 다를 수 있다.
  { key: 'in_progress', label: '진행 중', quick: 'open', color: COLORS.primary, title: '미완료(기한 지남 포함)로 거릅니다' },
  { key: 'overdue', label: '기한 지남', quick: 'overdue', color: COLORS.danger },
];

const avatar = {
  width: '36px', height: '36px', borderRadius: '50%', background: COLORS.bg, color: COLORS.textSub,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 600, flexShrink: 0,
};
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const sectionTitle = { margin: 0, fontSize: '15px', fontWeight: 700, color: COLORS.text };
const initialOf = (name) => String(name || '').trim().charAt(0);

// 임박 칩 — 지났으면 «N일 지남» · 오늘이면 «오늘» · 그 밖은 «D-N».
const chipOf = (daysLeft) => {
  const d = num(daysLeft);
  if (d < 0) return { text: `${-d}일 지남`, tone: 'danger' };
  if (d === 0) return { text: '오늘', tone: 'warn' };
  return { text: `D-${d}`, tone: 'default' };
};

export default function TaskPmDashboard({ projectId, refreshKey, onQuickFilter, onOpenTask }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [allMembers, setAllMembers] = useState(false);
  // 늦게 온 응답이 최신 집계를 덮지 않게 요청마다 번호를 매긴다.
  const seqRef = useRef(0);

  useEffect(() => {
    if (!projectId) return;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    // 🔴 보낼 때 오류를 비운다 — 한 번 실패한 문구가 다음 성공 뒤에 남지 않게.
    setError('');
    taskPmAPI.projectSummary(projectId)
      .then((res) => {
        if (seqRef.current !== seq) return;          // 지나간 요청의 응답은 버린다
        setData(normalize(res?.data));
      })
      .catch((e) => {
        if (seqRef.current !== seq) return;
        setData(null);
        setError(errorText(e, '대시보드를 불러오지 못했습니다.'));
      });
  }, [projectId, refreshKey]);

  const counts = data?.counts;
  const total = counts?.total || 0;
  const pctOf = (n) => (total ? Math.round((n / total) * 100) : 0);
  const pct = pctOf(counts?.done || 0);

  // 도넛 호 — 완료 → 진행 중 → 기한 지남 순으로 12시부터 이어 그린다(앞 호 길이만큼 offset).
  const segments = counts ? [
    { label: '완료', n: counts.done, color: COLORS.success },
    { label: '진행 중', n: counts.in_progress, color: COLORS.primary },
    { label: '기한 지남', n: counts.overdue, color: COLORS.danger },
  ] : [];
  let used = 0;
  const arcs = segments.map((s) => {
    // 🔴 합이 total 을 넘는 응답이어도 원 한 바퀴 밖으로 넘치지 않게 남은 길이로 자른다.
    const len = total ? Math.min((CIRCUMFERENCE * s.n) / total, Math.max(0, CIRCUMFERENCE - used)) : 0;
    const arc = { ...s, len, offset: used };
    used += len;
    return arc;
  });

  const members = data?.members || [];
  const shownMembers = allMembers ? members : members.slice(0, MEMBER_ROWS);
  const alerts = data?.alerts || [];
  const moreAlerts = data ? data.alertsTotal - alerts.length : 0;

  return (
    <section aria-label="프로젝트 대시보드" style={{ paddingTop: '24px' }}>
      {error && (
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{error}</div>
      )}
      {!data && !error && (
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textSub }}>불러오는 중…</p>
      )}

      {data && (
        <>
          {/* 1. 숫자 카드 4개(잔디 263×107 · 간격 24) — 누르면 그 조건으로 리스트가 열린다. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
            {CARDS.map((c) => {
              const n = counts[c.key];
              return (
                <button key={c.key} type="button" title={c.title}
                  aria-label={`${c.label} ${n}건 — 리스트로 보기`}
                  onClick={() => onQuickFilter && onQuickFilter(c.quick)}
                  style={{
                    ...card, width: '100%', minHeight: '107px', boxSizing: 'border-box', textAlign: 'left',
                    cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column',
                    justifyContent: 'space-between', gap: '8px',
                  }}>
                  <span style={{ fontSize: '13px', color: COLORS.textSub }}>{c.label}</span>
                  <span style={{ fontSize: '28px', fontWeight: 700, color: c.color }}>{n}</span>
                </button>
              );
            })}
          </div>

          {/* 2. 전체 완료율 · 멤버별 업무현황(잔디 550×302 · 간격 24) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))', gap: '24px', marginTop: '24px' }}>
            <div style={{ ...card, minHeight: '302px', boxSizing: 'border-box' }}>
              <h3 style={sectionTitle}>전체 완료율</h3>
              {/* 🔴 좁으면 범례가 도넛 아래로 내려간다(flexWrap) — 둘이 겹치지 않는다. */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '24px', marginTop: '16px' }}>
                <svg width="200" height="200" viewBox="0 0 200 200" role="img" aria-label={`전체 완료율 ${pct}%`}
                  style={{ flexShrink: 0 }}>
                  <circle cx="100" cy="100" r={RADIUS} fill="none" strokeWidth="24" stroke={COLORS.borderSoft} />
                  {arcs.filter((a) => a.len > 0).map((a) => (
                    <circle key={a.label} cx="100" cy="100" r={RADIUS} fill="none" strokeWidth="24" stroke={a.color}
                      strokeDasharray={`${a.len} ${CIRCUMFERENCE - a.len}`}
                      strokeDashoffset={-a.offset}
                      transform="rotate(-90 100 100)" />
                  ))}
                  <text x="100" y="100" textAnchor="middle" fontSize="28" fontWeight="700" fill={COLORS.text}>{`${pct}%`}</text>
                  <text x="100" y="124" textAnchor="middle" fontSize="12" fill={COLORS.textSub}>전체 완료율</text>
                </svg>
                {/* 범례 — 각각 반올림이라 합이 100 이 아닐 수 있다. */}
                <ul aria-label="완료율 범례" style={{ listStyle: 'none', margin: 0, padding: 0, flex: '1 1 160px', minWidth: 0 }}>
                  {segments.map((s) => (
                    <li key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '32px', fontSize: '13px', color: COLORS.text }}>
                      <span aria-hidden="true" style={{ width: '10px', height: '10px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                      <span>{s.label}</span>
                      <span style={{ marginLeft: 'auto', fontWeight: 600 }}>{`${pctOf(s.n)}%`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div style={{ ...card, minHeight: '302px', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3 style={sectionTitle}>멤버별 업무현황</h3>
                {/* 미배정 줄도 한 줄로 센다. */}
                {members.length > MEMBER_ROWS && (
                  <button type="button" aria-expanded={allMembers} onClick={() => setAllMembers((v) => !v)}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', color: COLORS.primaryDark, fontFamily: 'inherit' }}>
                    {allMembers ? '접기' : '전체보기'}
                  </button>
                )}
              </div>
              <ul aria-label="멤버별 업무현황" style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
                {shownMembers.map((m, i) => {
                  const name = m.name || (m.user_id == null ? '미배정' : '—');
                  const mTotal = num(m.total);
                  const mDone = num(m.done);
                  const fill = mTotal ? Math.min(100, (mDone / mTotal) * 100) : 0;
                  return (
                    <li key={m.user_id == null ? `none-${i}` : m.user_id}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', height: '62px', boxSizing: 'border-box' }}>
                      <span aria-hidden="true" style={avatar}>{initialOf(name)}</span>
                      <span title={name} style={{ ...ellipsis, width: '80px', flexShrink: 0, fontSize: '13px', color: COLORS.text }}>{name}</span>
                      <span aria-hidden="true" style={{ flex: 1, minWidth: 0, height: '8px', borderRadius: '999px', background: COLORS.bg, overflow: 'hidden' }}>
                        <span style={{ display: 'block', height: '100%', width: `${fill}%`, background: COLORS.primary, borderRadius: '999px' }} />
                      </span>
                      <span style={{ width: '44px', flexShrink: 0, textAlign: 'right', fontSize: '12px', color: COLORS.textSub }}>{`${mDone}/${mTotal}`}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* 3. 지연 및 임박 업무(폭 전체) — 행을 누르면 업무 상세. */}
          <div style={{ ...card, marginTop: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <h3 style={sectionTitle}>지연 및 임박 업무</h3>
              <span style={{ fontSize: '12px', color: COLORS.textSub }}>3일 이내 마감</span>
            </div>
            {alerts.length === 0
              ? <p style={{ margin: '12px 0 0', fontSize: '13px', color: COLORS.textSub }}>3일 안에 마감되는 업무가 없습니다.</p>
              : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '12px', marginTop: '12px' }}>
                  {alerts.map((a, i) => {
                    const who = a.assignee_name || '미배정';
                    const chip = chipOf(a.days_left);
                    return (
                      <button key={a.id ?? `alert-${i}`} type="button" onClick={() => onOpenTask && onOpenTask(a.id)}
                        style={{
                          minHeight: '75px', boxSizing: 'border-box', width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '12px 14px', border: `1px solid ${COLORS.borderSoft}`, borderRadius: '8px', background: COLORS.surface,
                          textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                        <span aria-hidden="true" style={avatar}>{initialOf(who)}</span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                          <span style={{ ...ellipsis, fontSize: '12px', color: COLORS.textSub }}>{who}</span>
                          <span style={{ ...ellipsis, fontSize: '14px', color: COLORS.text }}>{a.title}</span>
                        </span>
                        <span style={{ ...badge(chip.tone), flexShrink: 0 }}>{chip.text}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            {moreAlerts > 0 && (
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: COLORS.textSub }}>{`외 ${moreAlerts}건`}</p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
